import { ReactNode } from 'react';

import { LayoutArea, PanelState } from '../types';

export interface ContentEntry {
  id: string;
  content: ReactNode;
  priority: number;
  timestamp: number;
  source?: string;
  metadata?: Record<string, unknown>;
}

export interface ConflictResolution {
  strategy: 'replace' | 'tabs' | 'stack' | 'queue';
  activeEntry?: string;
  entries: ContentEntry[];
  reason: string;
}

export interface ContentConflictLog {
  timestamp: number;
  area: LayoutArea;
  action: 'conflict_detected' | 'resolution_applied' | 'content_switched';
  details: {
    conflictingIds: string[];
    resolution: ConflictResolution;
    previousActiveId?: string;
    newActiveId?: string;
  };
}

/**
 * Service responsible for resolving content conflicts when multiple components
 * try to render content in the same panel area.
 */
export class ContentConflictResolver {
  private static logs: ContentConflictLog[] = [];
  private static maxLogEntries = 100;

  /**
   * Resolves conflicts when new content is being set for a panel area
   * that already has existing content.
   */
  static resolveConflict(
    area: LayoutArea,
    newContent: ReactNode,
    newContentId: string,
    existingState: PanelState | undefined,
    options: {
      priority?: number;
      source?: string;
      metadata?: Record<string, unknown>;
      forceReplace?: boolean;
    } = {},
  ): ConflictResolution {
    const timestamp = Date.now();
    const { priority = 0, source, metadata, forceReplace = false } = options;

    // Create new content entry
    const newEntry: ContentEntry = {
      id: newContentId,
      content: newContent,
      priority,
      timestamp,
      source,
      metadata,
    };

    // If no existing content, no conflict
    if (!existingState?.content || !existingState.isVisible) {
      const resolution: ConflictResolution = {
        strategy: 'replace',
        activeEntry: newContentId,
        entries: [newEntry],
        reason: 'No existing content',
      };

      this.logConflict(area, 'resolution_applied', {
        conflictingIds: [newContentId],
        resolution,
        newActiveId: newContentId,
      });

      return resolution;
    }

    // Extract existing entries (may be from previous conflicts)
    const existingEntries = this.extractExistingEntries(existingState);

    // Force replace strategy
    if (forceReplace) {
      const resolution: ConflictResolution = {
        strategy: 'replace',
        activeEntry: newContentId,
        entries: [newEntry],
        reason: 'Force replace requested',
      };

      this.logConflict(area, 'resolution_applied', {
        conflictingIds: [newContentId, ...existingEntries.map((e) => e.id)],
        resolution,
        previousActiveId: existingEntries.find((e) => e.id === existingState.metadata?.activeEntryId)?.id,
        newActiveId: newContentId,
      });

      return resolution;
    }

    // Check if new content is identical to existing
    const identicalEntry = existingEntries.find((entry) => this.areContentsIdentical(entry.content, newContent));

    if (identicalEntry) {
      const resolution: ConflictResolution = {
        strategy: 'replace',
        activeEntry: identicalEntry.id,
        entries: existingEntries,
        reason: 'Identical content detected - switching to existing',
      };

      this.logConflict(area, 'content_switched', {
        conflictingIds: [newContentId, identicalEntry.id],
        resolution,
        previousActiveId: existingState.metadata?.activeEntryId as string,
        newActiveId: identicalEntry.id,
      });

      return resolution;
    }

    // Multiple different contents - use tabs strategy
    const allEntries = [...existingEntries, newEntry];
    const resolution: ConflictResolution = {
      strategy: 'tabs',
      activeEntry: newContentId, // New content becomes active
      entries: allEntries,
      reason: 'Multiple different contents detected',
    };

    this.logConflict(area, 'conflict_detected', {
      conflictingIds: allEntries.map((e) => e.id),
      resolution,
      previousActiveId: existingState.metadata?.activeEntryId as string,
      newActiveId: newContentId,
    });

    return resolution;
  }

  /**
   * Switches to a different content entry in a multi-content panel.
   */
  static switchToEntry(area: LayoutArea, targetEntryId: string, currentState: PanelState): ConflictResolution | null {
    const existingEntries = this.extractExistingEntries(currentState);
    const targetEntry = existingEntries.find((e) => e.id === targetEntryId);

    if (!targetEntry) {
      console.warn(`[ContentConflictResolver] Entry ${targetEntryId} not found in area ${area}`);
      return null;
    }

    const resolution: ConflictResolution = {
      strategy: 'tabs',
      activeEntry: targetEntryId,
      entries: existingEntries,
      reason: 'User-requested tab switch',
    };

    this.logConflict(area, 'content_switched', {
      conflictingIds: existingEntries.map((e) => e.id),
      resolution,
      previousActiveId: currentState.metadata?.activeEntryId as string,
      newActiveId: targetEntryId,
    });

    return resolution;
  }

  /**
   * Extracts content entries from existing panel state.
   */
  private static extractExistingEntries(state: PanelState): ContentEntry[] {
    // If state has conflict metadata, extract from there
    if (state.metadata?.conflictEntries) {
      return state.metadata.conflictEntries as ContentEntry[];
    }

    // Otherwise, create entry from current content
    return [
      {
        id: (state.metadata?.contentId as string) || 'existing',
        content: state.content,
        priority: 0,
        timestamp: Date.now() - 1000, // Slightly older than new content
        source: 'legacy',
      },
    ];
  }

  /**
   * Checks if two React content nodes are functionally identical.
   */
  private static areContentsIdentical(content1: ReactNode, content2: ReactNode): boolean {
    // Simple check - in a real implementation, you might want more sophisticated comparison
    if (content1 === content2) return true;

    // Check if both are React elements with same type and key
    if (typeof content1 === 'object' && content1 !== null && 'type' in content1 && typeof content2 === 'object' && content2 !== null && 'type' in content2) {
      return content1.type === content2.type && content1.key === content2.key;
    }

    return false;
  }

  /**
   * Logs conflict resolution events for debugging.
   */
  private static logConflict(area: LayoutArea, action: ContentConflictLog['action'], details: ContentConflictLog['details']): void {
    const logEntry: ContentConflictLog = {
      timestamp: Date.now(),
      area,
      action,
      details,
    };

    this.logs.push(logEntry);

    // Keep logs under limit
    if (this.logs.length > this.maxLogEntries) {
      this.logs = this.logs.slice(-this.maxLogEntries);
    }

    // Console logging for development
    console.log(`[ContentConflictResolver] ${action}:`, {
      area,
      ...details,
    });
  }

  /**
   * Gets recent conflict resolution logs for debugging.
   */
  static getLogs(limit?: number): ContentConflictLog[] {
    const logs = limit ? this.logs.slice(-limit) : [...this.logs];
    return logs.sort((a, b) => b.timestamp - a.timestamp);
  }

  /**
   * Clears conflict resolution logs.
   */
  static clearLogs(): void {
    this.logs = [];
  }

  /**
   * Gets debug information about current conflicts across all areas.
   */
  static getDebugInfo(): {
    totalConflicts: number;
    recentConflicts: ContentConflictLog[];
    activeResolutions: Record<string, number>;
  } {
    const recentConflicts = this.logs.slice(-10);
    const activeResolutions: Record<string, number> = {};

    this.logs.forEach((log) => {
      const strategy = log.details.resolution.strategy;
      activeResolutions[strategy] = (activeResolutions[strategy] || 0) + 1;
    });

    return {
      totalConflicts: this.logs.length,
      recentConflicts,
      activeResolutions,
    };
  }

  /**
   * Get summary of conflict resolution activity for debugging
   */
  static getConflictSummary() {
    return {
      totalConflicts: this.logs.length,
      resolvedConflicts: this.logs.filter((log) => log.action === 'resolution_applied').length,
      activeConflicts:
        this.logs.filter((log) => log.action === 'conflict_detected').length - this.logs.filter((log) => log.action === 'resolution_applied').length,
      resolutionStrategies: {
        replace: this.logs.filter((log) => log.details.resolution?.strategy === 'replace').length,
        tabs: this.logs.filter((log) => log.details.resolution?.strategy === 'tabs').length,
        stack: this.logs.filter((log) => log.details.resolution?.strategy === 'stack').length,
        queue: this.logs.filter((log) => log.details.resolution?.strategy === 'queue').length,
      },
      recentActivity: this.getLogs(10),
    };
  }
}
